import type { Tone } from '../../ui/index.js';
import type { QueueView } from './types.js';

export const VIEWS: readonly { key: QueueView; label: string; icon: string }[] = [
  { key: 'pending', label: 'À appeler', icon: 'phone' },
  { key: 'unreachable', label: 'Injoignables', icon: 'phone-off' },
  { key: 'callbacks', label: 'Rappels', icon: 'clock' },
  { key: 'confirmed', label: 'Confirmées', icon: 'check' },
  { key: 'shipped', label: 'Expédiées', icon: 'truck' },
  { key: 'cancelled', label: 'Annulées', icon: 'x' },
  { key: 'all', label: 'Toutes', icon: 'list' },
];

export const STATUS_DOT: Record<string, string> = {
  pending_call: 'bg-amber-500',
  unreachable: 'bg-red-500',
  callback: 'bg-blue-500',
  confirmed: 'bg-emerald-500',
  shipped: 'bg-teal-600',
  cancelled: 'bg-slate-500',
};

export const STATUS_PILL: Record<string, string> = {
  pending_call: 'bg-amber-50 text-amber-700 border-amber-200',
  unreachable: 'bg-red-50 text-red-700 border-red-200',
  callback: 'bg-blue-50 text-blue-700 border-blue-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  shipped: 'bg-teal-50 text-teal-700 border-teal-200',
  cancelled: 'bg-slate-100 text-slate-800 border-slate-300 font-semibold',
};

export const STATUS_STRIPE: Record<string, string> = {
  pending_call: 'bg-amber-500',
  unreachable: 'bg-red-500',
  callback: 'bg-blue-500',
  confirmed: 'bg-emerald-500',
  shipped: 'bg-teal-600',
  cancelled: 'bg-slate-400',
};

export const CONFIRMATION_TONE: Record<string, Tone> = {
  pending_call: 'signal',
  unreachable: 'alarm',
  callback: 'scan',
  confirmed: 'good',
  cancelled: 'neutral',
};

export const CONFIRMATION_LABEL: Record<string, string> = {
  pending_call: 'À appeler',
  unreachable: 'Injoignable',
  callback: 'Rappel',
  confirmed: 'Confirmée',
  cancelled: 'Annulée',
};

export const RISK_TONE: Record<string, Tone> = {
  vip_reliable: 'good',
  ghost_alert: 'alarm',
  high_risk: 'alarm',
  moderate_risk: 'signal',
  new_customer: 'scan',
  standard: 'neutral',
};

export const RISK_LABEL: Record<string, string> = {
  vip_reliable: 'VIP fiable',
  ghost_alert: 'Fantome',
  high_risk: 'Risque eleve',
  moderate_risk: 'Risque modere',
  new_customer: 'Nouveau client',
  standard: 'Standard',
};

export const RISK_BADGE: Record<string, string> = {
  vip_reliable: 'bg-emerald-50 text-emerald-700',
  ghost_alert: 'bg-red-50 text-red-700',
  high_risk: 'bg-red-50 text-red-700',
  moderate_risk: 'bg-amber-50 text-amber-700',
  new_customer: 'bg-blue-50 text-blue-700',
  standard: 'bg-gray-100 text-gray-600',
};

export const ACTION_LABEL: Record<string, string> = {
  assign: 'Prise en charge',
  attempt: 'Appel',
  note: 'Note',
  cancel: 'Annulation',
  confirm: 'Confirmation',
  parcel: 'Colis Jetpack',
  edit: 'Modification',
};

export function money(value: number | null): string {
  if (value === null) return '—';
  return `${value.toFixed(3).replace(/\.000$/, '')} DT`;
}

export function relative(iso: string | null): string {
  if (iso === null) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const minutes = Math.round((Date.now() - then) / 60000);
  if (minutes < 1) return "a l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  return `il y a ${days} j`;
}

export function shortDate(iso: string | null): string {
  if (iso === null) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export function lockLabel(lockedBy: string | null, lockExpiresAt: string | null): string {
  if (lockedBy === null) return '';
  const until = lockExpiresAt === null ? '' : ` - ${shortDate(lockExpiresAt)}`;
  return `verrouille par ${lockedBy}${until}`;
}